import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  TextField,
  Box,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Pagination,
  Paper,
  IconButton,
  Tooltip,
  Divider,
  CircularProgress,
  Alert,
  InputAdornment,
  Link,
} from '@mui/material';
import {
  Search as SearchIcon,
  Sort as SortIcon,
  FilterList as FilterIcon,
  Download as DownloadIcon,
  Visibility as VisibilityIcon,
  Bookmark as BookmarkIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import { useArticles } from '../hooks/useArticles';
import { PdfFile, Article } from '../services/articleService';
import { articleService } from '../services/articleService';

// Стилизованные компоненты
const FilterSection = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(3),
}));

const ArticleCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'transform 0.2s',
  '&:hover': {
    transform: 'translateY(-4px)',
  },
}));

const categories = [
  'Все категории',
  'Информационные технологии',
  'Экология',
  'Образование',
  'Медицина',
  'Экономика',
];

const years = ['Все годы', '2024', '2023', '2022'];

const sortOptions = [
  { value: 'date', label: 'По дате публикации' },
  { value: 'views', label: 'По просмотрам' },
  { value: 'downloads', label: 'По скачиваниям' },
  { value: 'citations', label: 'По цитированиям' },
];

const Articles: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('Все категории');
  const [selectedYear, setSelectedYear] = useState('Все годы');
  const [sortBy, setSortBy] = useState('date');
  const [page, setPage] = useState(1);
  const [limit] = useState(6);
  const [isSearching, setIsSearching] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const searchTimeoutRef = useRef<NodeJS.Timeout>();
  const searchInputRef = useRef<HTMLInputElement>(null);
  
  const {
    articles,
    loading,
    error,
    total,
    fetchArticles,
    page: currentPage,
  } = useArticles();

  const navigate = useNavigate();

  // Функция для выполнения поиска
  const performSearch = useCallback(async (query: string, currentPage: number = 1) => {
    try {
      setIsSearching(true);
      const filters = {
        search: query.trim(),
        category: selectedCategory !== 'Все категории' ? selectedCategory : undefined,
        year: selectedYear !== 'Все годы' ? selectedYear : undefined,
        sortBy,
        page: currentPage,
        limit: 6,
      };
      console.log('Отправка запроса поиска с параметрами:', filters);
      await fetchArticles(filters);
    } finally {
      setIsSearching(false);
      setIsInitialLoad(false);
    }
  }, [selectedCategory, selectedYear, sortBy, fetchArticles]);

  // Обработка изменения поискового запроса
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);
    setPage(1); // Сбрасываем страницу при поиске

    // Очищаем предыдущий таймаут
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Устанавливаем новый таймаут для поиска
    searchTimeoutRef.current = setTimeout(() => {
      performSearch(value, 1);
    }, 500);
  };

  // Обработка очистки поиска
  const handleClearSearch = () => {
    setSearchQuery('');
    setPage(1); // Сбрасываем страницу при очистке
    performSearch('', 1);
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };

  // Обработка отправки формы поиска
  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    setPage(1); // Сбрасываем страницу при отправке формы
    performSearch(searchQuery, 1);
  };

  // Обработка изменения страницы
  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
    performSearch(searchQuery, value);
  };

  // Загрузка статей при изменении фильтров
  useEffect(() => {
    if (!isInitialLoad) {
      setPage(1); // Сбрасываем страницу при изменении фильтров
      performSearch(searchQuery, 1);
    }
  }, [selectedCategory, selectedYear, sortBy, performSearch, searchQuery, isInitialLoad]);

  // Очистка таймаута при размонтировании
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  // Обработка изменения фильтров
  const handleFilterChange = () => {
    setPage(1); // Сброс на первую страницу при изменении фильтров
    if (!searchQuery) { // Применяем фильтры только если нет поискового запроса
      fetchArticles({
        category: selectedCategory !== 'Все категории' ? selectedCategory : undefined,
        year: selectedYear !== 'Все годы' ? selectedYear : undefined,
        sortBy,
        page: 1,
        limit: 6,
      });
    }
  };

  // Обработка изменения категории
  const handleCategoryChange = (e: any) => {
    setSelectedCategory(e.target.value);
    handleFilterChange();
  };

  // Обработка изменения года
  const handleYearChange = (e: any) => {
    setSelectedYear(e.target.value);
    handleFilterChange();
  };

  // Обработка изменения сортировки
  const handleSortChange = (e: any) => {
    setSortBy(e.target.value);
    handleFilterChange();
  };

  const handleDownloadPdf = async (article: Article) => {
    console.log('Попытка скачать PDF для статьи:', {
      id: article.id,
      title: article.title,
      pdfFile: article.pdfFile
    });
    
    if (article.pdfFile?.file) {
      try {
        // Увеличиваем счетчик скачиваний
        await articleService.incrementDownloads(article.id);
        
        // Формируем URL для открытия PDF
        const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';
        const pdfUrl = `${baseUrl}/uploads/${article.pdfFile.file}`;
        
        // Открываем PDF в новой вкладке
        window.open(pdfUrl, '_blank');
      } catch (error) {
        console.error('Ошибка при скачивании PDF:', error);
      }
    } else {
      console.error('PDF файл не найден для статьи:', article.id);
    }
  };

  const handleArticleClick = (article: Article) => {
    const slug = articleService.createSlug(article.title);
    navigate(`/articles/${article.id}/${slug}`);
  };

  if (loading && isInitialLoad) {
    return (
      <Container sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    return (
      <Container sx={{ py: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  return (
    <Container sx={{ py: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Статьи
      </Typography>

      <FilterSection elevation={1}>
        <form onSubmit={handleSearchSubmit}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                inputRef={searchInputRef}
                fullWidth
                placeholder="Поиск по названию, авторам, ключевым словам..."
                value={searchQuery}
                onChange={handleSearchChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: 'text.secondary' }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      {isSearching && <CircularProgress size={20} />}
                      {searchQuery && (
                        <IconButton
                          size="small"
                          onClick={handleClearSearch}
                          edge="end"
                        >
                          <ClearIcon />
                        </IconButton>
                      )}
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth>
                <InputLabel>Категория</InputLabel>
                <Select
                  value={selectedCategory}
                  label="Категория"
                  onChange={handleCategoryChange}
                >
                  {categories.map((category) => (
                    <MenuItem key={category} value={category}>
                      {category}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth>
                <InputLabel>Год</InputLabel>
                <Select
                  value={selectedYear}
                  label="Год"
                  onChange={handleYearChange}
                >
                  {years.map((year) => (
                    <MenuItem key={year} value={year}>
                      {year}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth>
                <InputLabel>Сортировка</InputLabel>
                <Select
                  value={sortBy}
                  label="Сортировка"
                  onChange={handleSortChange}
                >
                  {sortOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </form>
      </FilterSection>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Grid container spacing={4}>
            {articles.map((article) => (
              <Grid key={article.id} item xs={12} sm={6} lg={4}>
                <ArticleCard>
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Typography
                      variant="h6"
                      component="h2"
                      gutterBottom
                      sx={{
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        lineHeight: '1.4',
                        height: '4.2em'
                      }}
                    >
                      <Link
                        component={RouterLink}
                        to={`/articles/${article.id}/${articleService.createSlug(article.title)}`}
                        color="inherit"
                        underline="none"
                      >
                        {article.title}
                      </Link>
                    </Typography>
                    <Typography 
                      variant="body2" 
                      color="text.secondary" 
                      paragraph
                      sx={{
                        display: '-webkit-box',
                        WebkitLineClamp: 4,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        height: '6em', // 4 строки * 1.5 line-height
                        lineHeight: '1.5em'
                      }}
                    >
                      {article.abstract}
                    </Typography>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Ключевые слова:
                      </Typography>
                      <Box 
                        sx={{ 
                          display: 'flex', 
                          flexWrap: 'wrap', 
                          gap: 1,
                          height: '64px', // Увеличиваем высоту для полного отображения двух строк
                          overflow: 'hidden'
                        }}
                      >
                        {Array.isArray(article.keywords) && article.keywords.map((keyword, index) => (
                          <Chip
                            key={index}
                            label={keyword}
                            size="small"
                            color="primary"
                            variant="outlined"
                            sx={{ 
                              maxWidth: '100%',
                              '& .MuiChip-label': {
                                whiteSpace: 'normal',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }
                            }}
                          />
                        ))}
                      </Box>
                    </Box>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          <VisibilityIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                          {article.views}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          <DownloadIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                          {article.downloads}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          {article.issue ? `Выпуск ${article.issue.number}, ${article.issue.year}` : 'Не опубликовано'}
                        </Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                  <Divider />
                  <CardActions>
                    <Button
                      component={RouterLink}
                      to={`/articles/${article.id}/${articleService.createSlug(article.title)}`}
                      size="small"
                      color="primary"
                    >
                      Читать полностью
                    </Button>
                    {article.pdfFile?.path && (
                      <Button
                        onClick={() => handleDownloadPdf(article)}
                        size="small"
                        color="secondary"
                        startIcon={<DownloadIcon />}
                      >
                        Скачать PDF
                      </Button>
                    )}
                    <Tooltip title="Сохранить">
                      <IconButton size="small">
                        <BookmarkIcon />
                      </IconButton>
                    </Tooltip>
                  </CardActions>
                </ArticleCard>
              </Grid>
            ))}
          </Grid>

          {articles.length === 0 && !loading && (
            <Typography variant="body1" sx={{ mt: 4, textAlign: 'center' }}>
              {searchQuery 
                ? 'По вашему запросу ничего не найдено. Попробуйте изменить параметры поиска.' 
                : 'Статьи не найдены'}
            </Typography>
          )}

          {total > 0 && !loading && (
            <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Показано {articles.length} из {total} статей
              </Typography>
              <Pagination
                count={Math.ceil(total / 6)}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
                size="large"
                showFirstButton
                showLastButton
                siblingCount={1}
                boundaryCount={1}
              />
            </Box>
          )}
        </>
      )}
    </Container>
  );
};

export default Articles; 